import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Highcharts from 'highcharts';
import HC_more from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
HC_more(Highcharts);

export default function PieChart({ data, filters, institutions }) {
  const [screenWidth, setScreenWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const chartRef = useRef(null);
  const [chartInstance, setChartInstance] = useState(null);

  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chart && !chartInstance) {
      setChartInstance(chartRef.current.chart);
    }
  }, [chartRef.current]);

  useEffect(() => {
    if (chartRef.current && chartRef.current.chart) {
      const chart = chartRef.current.chart;
      chart.update(
        {
          series: [
            {
              name: 'Total',
              colorByPoint: true,
              data: data.map(item => ({
                name: item.gre,
                y: item.total_users
              }))
            }
          ]
        },
        true
      );
    }
  }, [data]);

  const getSelectedGres = useCallback(() => {
    const directGres = filters.filter(f => f.type === 'regional').map(f => f.label);

    const institutionFilters = filters.filter(f => f.type === 'institution');
    let institutionGres = [];

    if (institutionFilters.length > 0 && institutions && institutions.length > 0) {
      institutionGres = institutionFilters
        .map(filter => {
          const institution = institutions.find(inst => inst.label === filter.label);
          return institution ? institution.gre : null;
        })
        .filter(gre => gre !== null);
    }

    return [...new Set([...directGres, ...institutionGres])];
  }, [filters, institutions]);

  const hasActiveFilters = useCallback(() => {
    const selectedGres = getSelectedGres();
    return selectedGres.length > 0;
  }, [getSelectedGres]);

  useEffect(() => {
    if (!chartRef.current || !data || !chartInstance) return;

    const chart = chartInstance;
    const series = chart.series[0];
    if (!series) return;

    const points = series.points;
    const selectedGres = getSelectedGres();
    const hasFilters = hasActiveFilters();

    const setElementOpacity = (element, opacity) => {
      if (!element) return;

      try {
        if (element.element && element.element.style) {
          element.element.style.opacity = opacity;
          element.element.style.transition = 'none';
        }

        if (element.style) {
          element.style.opacity = opacity;
          element.style.transition = 'none';
        }

        if (element.legendItem && element.legendItem.element) {
          element.legendItem.element.style.opacity = opacity;
          element.legendItem.element.style.transition = 'none';
        }

        if (element.legendSymbol && element.legendSymbol.element) {
          element.legendSymbol.element.style.opacity = opacity;
          element.legendSymbol.element.style.transition = 'none';
        }

        if (element.animate) {
          element.animate({ opacity: opacity });
        } else if (element.attr) {
          element.attr({ opacity: opacity });
        }
      } catch (error) {
        console.warn('Error setting opacity:', error);
      }
    };

    if (hasFilters) {
      points.forEach(point => {
        const isSelected = selectedGres.includes(point.name);

        if (isSelected) {
          setElementOpacity(point.graphic, 1);
          setElementOpacity(point.legendItem, 1);
          setElementOpacity(point.legendSymbol, 1);

          point.setState('normal');

          if (point.graphic && point.graphic.element) {
            point.graphic.element.classList.add('force-visible');
            point.graphic.element.classList.remove('force-dimmed');
          }
        } else {
          setElementOpacity(point.graphic, 0.3);
          setElementOpacity(point.legendItem, 0.3);
          setElementOpacity(point.legendSymbol, 0.3);

          point.setState('normal');

          if (point.graphic && point.graphic.element) {
            point.graphic.element.classList.add('force-dimmed');
            point.graphic.element.classList.remove('force-visible');
          }
        }
      });

      chart.update(
        {
          plotOptions: {
            series: {
              states: {
                hover: {
                  enabled: false
                },
                inactive: {
                  opacity: 1
                }
              }
            },
            pie: {
              states: {
                hover: {
                  enabled: false
                }
              }
            }
          }
        },
        false
      );
    } else {
      points.forEach(point => {
        setElementOpacity(point.graphic, 1);
        setElementOpacity(point.legendItem, 1);
        setElementOpacity(point.legendSymbol, 1);

        point.setState('normal');

        if (point.graphic && point.graphic.element) {
          point.graphic.element.classList.remove('force-visible', 'force-dimmed');
        }
      });

      chart.update(
        {
          plotOptions: {
            series: {
              states: {
                hover: {
                  enabled: true
                }
              }
            },
            pie: {
              states: {
                hover: {
                  enabled: true
                }
              }
            }
          }
        },
        false
      );
    }

    chart.redraw();
  }, [filters, institutions, data, chartInstance, getSelectedGres, hasActiveFilters]);

  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .highcharts-point.force-visible {
        opacity: 1 !important;
      }
      .highcharts-point.force-dimmed {
        opacity: 0.3 !important;
      }
      .highcharts-legend-item.force-visible {
        opacity: 1 !important;
      }
      .highcharts-legend-item.force-dimmed {
        opacity: 0.3 !important;
      }
      .highcharts-data-label.force-visible {
        opacity: 1 !important;
      }
      .highcharts-data-label.force-hidden {
        opacity: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const options = useMemo(() => {
    const series = data?.map(item => ({
      name: item.gre,
      y: item['total_users']
    }));

    return {
      chart: {
        type: 'pie',
        events: {
          load: function () {
            setChartInstance(this);
          },
          redraw: function () {
            const hasFilters = hasActiveFilters();
            const selectedGres = getSelectedGres();

            setTimeout(() => {
              if (!this.series[0]) return;

              this.series[0].points.forEach(point => {
                const isSelected = selectedGres.includes(point.name);

                if (point.graphic && point.graphic.element) {
                  if (hasFilters) {
                    if (isSelected) {
                      point.graphic.element.style.opacity = '1';
                      point.graphic.element.classList.add('force-visible');
                      point.graphic.element.classList.remove('force-dimmed');
                    } else {
                      point.graphic.element.style.opacity = '0.3';
                      point.graphic.element.classList.add('force-dimmed');
                      point.graphic.element.classList.remove('force-visible');
                    }
                  } else {
                    // SEM filtros: todos visíveis (opacidade 1)
                    point.graphic.element.style.opacity = '1';
                    point.graphic.element.classList.remove('force-visible', 'force-dimmed');
                  }
                }

                // Controle de dataLabels
                const dataLabel = point.dataLabel;
                if (dataLabel && dataLabel.element) {
                  if (hasFilters && isSelected) {
                    dataLabel.element.style.opacity = '1';
                    dataLabel.element.classList.add('force-visible');
                    dataLabel.element.classList.remove('force-hidden');
                  } else {
                    dataLabel.element.style.opacity = '0';
                    dataLabel.element.classList.add('force-hidden');
                    dataLabel.element.classList.remove('force-visible');
                  }
                }
              });
            }, 10);
          }
        }
      },
      credits: { enabled: false },
      title: { text: null },
      series: [
        {
          name: 'Total',
          colorByPoint: true,
          data: series,
          dataLabels: {
            enabled: true
          },
          states: {
            hover: {
              enabled: true,
              brightness: 0.1
            },
            inactive: {
              opacity: 1
            }
          }
        }
      ],

      legend: {
        enabled: true,
        align: 'right',
        verticalAlign: 'middle',
        layout: 'horizontal',
        width: 600,
        itemWidth: 300,
        itemMarginBottom: 5
      },

      plotOptions: {
        pie: {
          innerSize: '40%',
          allowPointSelect: false,
          cursor: function () {
            const hasFilters = hasActiveFilters();
            return hasFilters ? 'default' : 'pointer';
          },
          dataLabels: {
            enabled: true,
            style: {
              fontWeight: 'bold',
              textOutline: 'none',
              fontSize: '11px',
              color: '#333'
            },
            formatter: function () {
              const selectedGres = getSelectedGres();
              const hasFilters = hasActiveFilters();

              if (hasFilters) {
                return selectedGres.includes(this.point.name) ? `${this.y}` : null;
              }

              return null;
            },
            distance: 20,
            connectorWidth: 1,
            connectorColor: '#666',
            softConnector: false
          },
          showInLegend: true,
          point: {
            events: {
              mouseOver: function () {
                const hasFilters = hasActiveFilters();
                if (hasFilters) {
                  return false;
                }
              },
              mouseOut: function () {
                const hasFilters = hasActiveFilters();
                if (hasFilters) {
                  return false;
                }
              }
            }
          },
          states: {
            hover: {
              enabled: true,
              brightness: 0.1
            },
            inactive: {
              opacity: 1
            }
          }
        }
      },

      responsive: {
        rules: [
          {
            condition: { maxWidth: 768 },
            chartOptions: {
              chart: { marginBottom: 200 },
              legend: {
                align: 'center',
                verticalAlign: 'bottom',
                layout: 'vertical',
                width: 300,
                maxHeight: 160,
                marginTop: 20
              },
              plotOptions: {
                pie: {
                  dataLabels: {
                    distance: 10,
                    style: {
                      fontSize: '10px'
                    }
                  }
                }
              }
            }
          }
        ]
      }
    };
  }, [screenWidth, data, getSelectedGres, hasActiveFilters]);

  useEffect(() => {
    if (chartInstance) {
      chartInstance.currentFilters = filters;
      chartInstance.currentInstitutions = institutions;
      chartInstance.getSelectedGres = getSelectedGres;
      chartInstance.hasActiveFilters = hasActiveFilters;

      if (chartInstance.series && chartInstance.series[0]) {
        chartInstance.redraw();
      }
    }
  }, [filters, institutions, chartInstance, getSelectedGres, hasActiveFilters]);

  return (
    <HighchartsReact
      highcharts={Highcharts}
      options={options}
      ref={chartRef}
    />
  );
}
